import { Dashboard } from '../types/gotypes'
import { refuse, type WhoToAsk } from './permissionReason'

/**
 * Mirrors the backend's canOverwriteDashboard (goapi/pkg/server/dashboard_ownership.go)
 * so the editor can decide up front whether to save in place or fork (#962),
 * without waiting on a round-trip 403 for the common case. The server is the
 * source of truth and enforces the same rule independently.
 */
export function canOverwriteDashboard(
  dashboard: Dashboard | null | undefined,
  userEmail: string,
  userCanPromote: boolean
): boolean {
  if (!dashboard) return false
  if (dashboard.user === userEmail) return true
  if (dashboard.promoted_at && userCanPromote) return true
  return false
}

/**
 * #1219 gate 2: the `code` a regenerate start (`.../analysis/start`) answers
 * with when the caller may not overwrite the dashboard the card lives on —
 * the same rule `canOverwriteDashboard` computes client-side, enforced
 * server-side as the source of truth. Key on this, not on the HTTP status:
 * a 403 can mean other things (e.g. auth), this code means specifically
 * "you can't regenerate this card."
 */
export const REGENERATE_NOT_PERMITTED_CODE = 'regenerate_not_permitted'

/**
 * The person to ask about a dashboard the viewer cannot change. A private
 * colleague's dashboard names its owner. A company-level one has no owner to
 * ask (only a Manager can change it), so it names the viewer's team manager.
 */
const askOwnerOf = (dashboard: Dashboard | null | undefined) => (dashboard?.promoted_at ? undefined : dashboard?.user || undefined)

/**
 * Why a disabled Edit control is disabled (#1262, Adam 8 Oct: disable and say
 * why, never hide). Points at Duplicate, the route to an editable copy.
 */
export const noEditPermissionReason = (who: WhoToAsk, dashboard?: Dashboard | null): string =>
  refuse("You can't edit this dashboard.", who, { owner: askOwnerOf(dashboard), copy: 'Duplicate' }).reason

/**
 * Why a disabled dashboard Sign off is disabled. Signing off saves the
 * dashboard, so the server allows it only to someone who can edit it in
 * place. A copy does not help (it would sign off the copy), so none is offered.
 */
export const noSignOffDashboardReason = (who: WhoToAsk, dashboard?: Dashboard | null): string =>
  refuse("You can't sign off this dashboard.", who, { owner: askOwnerOf(dashboard) }).reason

/** Why a disabled Delete control is disabled. A copy does not help, so none is offered. */
export const noDeleteDashboardReason = (who: WhoToAsk, dashboard?: Dashboard | null): string =>
  refuse("You can't delete this dashboard.", who, { owner: askOwnerOf(dashboard) }).reason

/**
 * The message for a name clash (#1274), or null when the error is anything
 * else — including the optimistic-concurrency 409 on the same PUT route,
 * which the update mutation answers by reloading. The server marks a name
 * clash with `conflict.kind = "name"` and names the owner, who is the person
 * to ask; the user's draft is kept so they can choose another name.
 */
export function dashboardNameConflictMessage(error: unknown): string | null {
  const e = error as { isAxiosError?: boolean; response?: { status?: number; data?: { conflict?: { kind?: string; dashboard_name?: string; owner?: string } } } } | undefined
  const conflict = e?.response?.data?.conflict
  if (!e?.isAxiosError || e.response?.status !== 409 || conflict?.kind !== 'name') return null
  const owned = conflict.owner ? `, owned by ${conflict.owner}` : ''
  return `A dashboard named "${conflict.dashboard_name}" already exists${owned}. Choose a different name.`
}
